/**
 * Base API client abstraction - reusable across any API.
 *
 * This file contains the generic `ApiClient` class and related types that can be used
 * to create API clients for any API. It provides a consistent interface with support for:
 * - Context-based request configuration
 * - Custom header management
 * - Request/response transformations
 * - URL transformations
 * - Error handling
 *
 * For Intercom-specific implementation, see `api-client.ts`.
 */

type Method = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

type RequestBody = Record<string, unknown> | FormData | undefined;

interface ApiClientOptions<TContext, TError extends Error> {
  /**
   * Initial context data that is used to make global data available across all requests, e.g. appId.
   * This simplifies the construction of request URLS. Context can be updated later using setContext().
   *
   * @example
   * ```ts
   * context: { appId: '123' }
   * ```
   */
  context: TContext;

  /**
   * Function which defines how headers are built for all requests.
   * Used to provide default headers and the restriction of allowed headers.
   *
   * @example
   * ```ts
   * buildHeaders: (customHeaders) => {
   *   return {
   *     ...customHeaders,
   *     'Content-Type': 'application/json',
   *   };
   * }
   * ```
   */
  buildHeaders: (customHeaders?: Record<string, string>) => Record<string, string>;

  /**
   * Function which transforms the url for all requests. Can be considered as a global
   * url function. Only applied to static urls.
   *
   * @example
   * ```ts
   * transformUrl: (url, context) => {
   *   return url + `/api/v2/${context.appId}`;
   * }
   * ```
   */
  transformUrl?: (url: string, context: TContext) => string;

  /**
   * Function which transforms successful (`response.ok === true`) response data for
   * all requests. Useful for applying global transformations like camelCase or snakeCase
   * or converting response data from text to JSON.
   *
   * @example
   * ```ts
   * transformResponse: async (response) => {
   *   const text = await response.text();
   *   return JSON.parse(text) as unknown;
   * }
   * ```
   */
  transformResponse: (response: Response) => Promise<unknown>;

  /**
   * Function which transforms error responses (`response.ok === false`) into Error
   * instances. Useful for applying global transformations like camelCase or snakeCase
   * or converting error response data from text to JSON.
   *
   * @example
   * ```ts
   * transformError: async (response) => {
   *   const text = await response.text();
   *   const message = JSON.parse(text).message;
   *   return new Error(message);
   * }
   * ```
   */
  transformError: (response: Response) => Promise<TError>;

  /**
   * Function which transforms request body for all requests which have a
   * body which is a plain object. Useful for applying global transformations
   * like camelCase or snake_case.
   *
   * @note This function is not applied to requests which have a body of type `FormData`.
   *
   * @example
   * ```ts
   * transformRequestBody: (body, context) => {
   *   return decamelizeKeys(body, { deep: true });
   * }
   * ```
   *
   * @example
   * ```ts
   * transformRequestBody: (body, context, method) => {
   *   if (method === 'POST' || method === 'PUT') {
   *     return { ...body, app_id: context.appId };
   *   }
   *   return body;
   * }
   * ```
   */
  transformRequestBody: (
    body: Record<string, unknown>,
    context: TContext,
    method: Method,
  ) => Record<string, unknown>;

  /**
   * Function which transforms request body for all requests which have a
   * body which is of type `FormData`.
   *
   * @note This function is not applied to requests which have plain object bodies.
   *
   * @example
   * ```ts
   * transformRequestFormData: (formData, context, method) => {
   *   formData.append('app_id', context.appId);
   *   return formData;
   * }
   * ```
   */
  transformRequestFormData: (formData: FormData, context: TContext, method: Method) => FormData;
}

type FetchOptions = NonNullable<Parameters<typeof window.fetch>[1]>;

/**
 * Options for `ApiClient.<requestMethod>` methods. Omits certain values from
 * `window.fetch` options which are handled by the ApiClient.
 */
type ApiClientRequestOptions<TContext = unknown> = Omit<FetchOptions, 'body' | 'method'> & {
  headers?: Record<string, string>;
  transformRequestBody?: (
    body: Record<string, unknown>,
    context: TContext,
    method: Method,
  ) => Record<string, unknown>;
  transformResponse?: (response: Response) => Promise<unknown>;
};

type ApiClientUrl<TContext> = string | ((context: TContext) => string);

/**
 * Generic API client abstraction which can be used to create API clients
 * for any API.
 *
 * Provides mechanisms for defining global rules and transformations for
 * the API which typically do not change for each request.
 *
 * @example Basic usage with static URLs
 * ```ts
 * await apiClient.get('/api/v1/users');
 * await apiClient.post('/api/v1/users', { name: 'John Doe' });
 * await apiClient.put('/api/v1/users/123', { name: 'Jane Doe' });
 * await apiClient.patch('/api/v1/users/123', { name: 'Jane Smith' });
 * await apiClient.delete('/api/v1/users/123');
 * ```
 *
 * @example Dynamic URLs using context
 * ```ts
 * await apiClient.get(({ appId }) => `/api/v1/apps/${appId}/users`);
 * await apiClient.post(({ appId }) => `/api/v1/apps/${appId}/users`, { name: 'John' });
 * ```
 *
 * @example With custom configuration
 * ```ts
 * await apiClient.get('/api/v1/users', { cache: 'no-store' });
 * ```
 *
 * @example Error handling
 * ```ts
 * try {
 *   await apiClient.post('/api/v1/users', { name: 'John' });
 * } catch (error) {
 *   console.log('Error status:', error.status);
 *   console.log('Error body:', error.body);
 * }
 * ```
 *
 * @example Per-request transformation overrides
 * ```ts
 * // Skip default transformations for a specific request
 * const result = await apiClient.post('/api/legacy', body, {
 *   transformRequestBody: (body, context) => ({ ...body, app_id: context.appId }),
 *   transformResponse: async (response) => JSON.parse(await response.text())
 * });
 *
 * // Only override request transformation
 * await apiClient.put('/api/custom', data, {
 *   transformRequestBody: (body) => body // No transformation
 * });
 *
 * // Only override response transformation
 * const csvData = await apiClient.get('/api/export', {
 *   transformResponse: async (response) => await response.text()
 * });
 * ```
 *
 * @see `ApiClientOptions` for configuration details.
 */
export class ApiClient<TContext, TError extends Error> {
  private readonly options: ApiClientOptions<TContext, TError>;
  private context: TContext;

  constructor(options: ApiClientOptions<TContext, TError>) {
    this.options = options;
    this.context = options.context;
  }

  /**
   * Updates the context used for all subsequent requests.
   *
   * @param context - The new context to use for requests.
   */
  public setContext(context: TContext): void {
    this.context = context;
  }

  /**
   * Performs a GET request.
   *
   * @param url - URL or function which returns a URL.
   * @param requestOptions - Optional request configuration.
   * @see {@link ApiClient} for usage examples.
   */
  public async get<TResponseData = unknown>(
    url: ApiClientUrl<TContext>,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    return this.request('GET', url, undefined, requestOptions);
  }

  /**
   * Performs a POST request.
   *
   * @param url - URL or function which returns a URL.
   * @param payload - Request body (optional). Does not need to be stringified.
   * @param requestOptions - Optional request configuration.
   * @see {@link ApiClient} for usage examples.
   */
  public async post<TResponseData = unknown>(
    url: ApiClientUrl<TContext>,
    payload?: RequestBody,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    return this.request('POST', url, payload, requestOptions);
  }

  /**
   * Performs a PUT request.
   *
   * @param url - URL or function which returns a URL.
   * @param payload - Request body (optional). Does not need to be stringified.
   * @param requestOptions - Optional request configuration.
   * @see {@link ApiClient} for usage examples.
   */
  public async put<TResponseData = unknown>(
    url: ApiClientUrl<TContext>,
    payload?: RequestBody,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    return this.request('PUT', url, payload, requestOptions);
  }

  /**
   * Performs a PATCH request.
   *
   * @param url - URL or function which returns a URL.
   * @param payload - Request body (optional). Does not need to be stringified.
   * @param requestOptions - Optional request configuration.
   * @see {@link ApiClient} for usage examples.
   */
  public async patch<TResponseData = unknown>(
    url: ApiClientUrl<TContext>,
    payload?: RequestBody,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    return this.request('PATCH', url, payload, requestOptions);
  }

  /**
   * Performs a DELETE request.
   *
   * @param url - URL or function which returns a URL.
   * @param requestOptions - Optional request configuration.
   * @see {@link ApiClient} for usage examples.
   */
  public async delete<TResponseData = unknown>(
    url: ApiClientUrl<TContext>,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    return this.request('DELETE', url, undefined, requestOptions);
  }

  private async request<TResponseData = unknown>(
    method: Method,
    urlOrUrlFunction: ApiClientUrl<TContext>,
    payload?: RequestBody,
    requestOptions?: ApiClientRequestOptions<TContext>,
  ): Promise<TResponseData> {
    const isFormDataRequest = this.isFormDataRequest(payload);

    const transformRequestBody =
      requestOptions?.transformRequestBody ?? this.options.transformRequestBody;
    const body = this.transformRequestBody(payload, this.context, method, transformRequestBody);

    const url = this.resolveUrl(urlOrUrlFunction);
    const headers = this.options.buildHeaders(requestOptions?.headers);

    if (isFormDataRequest && headers['Content-Type'] === 'application/json') {
      // This is required because the browser must calculate the multipart/form-data
      // Content-Type header when a FormData object is passed to fetch.
      delete headers['Content-Type'];
    }

    if (isFormDataRequest) {
      // This is required because the browser must calculate the multipart/form-data
      // Content-Type header when a FormData object is passed to fetch.
      delete headers['Content-Type'];
    } else {
      headers['Content-Type'] = 'application/json';
    }

    const options = {
      ...requestOptions,
      method,
      body,
      headers,
    };

    // Engineers are encouraged to use an ApiClient instance rather than window.fetch.
    // This ensures that API usage is consistent and that the CSRF token is always included.
    // eslint-disable-next-line no-restricted-properties
    const response = await window.fetch(url, options);

    if (!response.ok) {
      const transformedError = await this.options.transformError(response);
      throw transformedError;
    }

    const transformResponse = requestOptions?.transformResponse ?? this.options.transformResponse;
    const data = (await transformResponse(response)) as TResponseData;

    return data;
  }

  private resolveUrl(urlOrUrlFunction: ApiClientUrl<TContext>): string {
    return typeof urlOrUrlFunction === 'function'
      ? this.resolveUrlFunction(urlOrUrlFunction)
      : this.resolveUrlString(urlOrUrlFunction);
  }

  private resolveUrlFunction(urlFn: (context: TContext) => string): string {
    return urlFn(this.context);
  }

  private resolveUrlString(url: string): string {
    // Apply global url transformation if defined
    return this.options.transformUrl ? this.options.transformUrl(url, this.context) : url;
  }

  private transformRequestBody(
    payload: RequestBody,
    context: TContext,
    method: Method,
    transformFn: (
      body: Record<string, unknown>,
      context: TContext,
      method: Method,
    ) => Record<string, unknown>,
  ): BodyInit | undefined {
    if (!payload) {
      return undefined;
    }
    if (this.isFormDataRequest(payload)) {
      return this.options.transformRequestFormData(payload, context, method);
    }
    return JSON.stringify(transformFn(payload, context, method));
  }

  private isFormDataRequest(payload?: RequestBody): payload is FormData {
    return payload !== undefined && payload instanceof FormData;
  }
}
